import { FunctionComponent, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "@/redux/api";
import { useAppDispatch } from "@/redux/hooks";

import { SignalScanSocketProvider } from "../providers/SignalScanSocketContext";
import { TrackerSignalSearchScope } from "../providers/TrackerSignalSearchScope";
import { TrackerDetailsTable } from "./TrackerDetailsTable";
import { TrackerEmptyState } from "./TrackerEmptyState";
import { SearchForCompanyModal } from "./CustomColumns/Modals/SearchForCompanyModal";

interface TrackedProfileTabProps {
	/** List id (from the route) — drives the "Add a source" navigation. */
	listId?: string;
	/** Distinct tracked-company count from the lean list (arrives as a string at runtime). */
	trackerOrganizationCount?: number;
	/** Trackers attached to the list (the table needs the first tracker's id). */
	trackers?: { id: number }[];
}

/**
 * Content for the list-detail "Tracked Profiles" tab (PREC-3614). When the tracker has companies it
 * renders the tracker table; otherwise it shows an empty state that offers adding a company or a
 * source — the lead-centric "list is empty" state never applies to Profile Tracker lists.
 */
export const TrackedProfileTab: FunctionComponent<TrackedProfileTabProps> = ({
	listId,
	trackerOrganizationCount,
	trackers,
}) => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [addCompanyOpen, setAddCompanyOpen] = useState(false);
	const trackerId = trackers?.[0]?.id;

	// API aggregate counts arrive as strings — coerce before comparing.
	const companyCount = Number(trackerOrganizationCount ?? 0) || 0;

	if (companyCount > 0 && trackers && trackers.length > 0) {
		return (
			<SignalScanSocketProvider>
				<TrackerSignalSearchScope trackerId={trackers[0].id}>
					<TrackerDetailsTable trackerId={trackers[0].id} />
				</TrackerSignalSearchScope>
			</SignalScanSocketProvider>
		);
	}

	// The tab shows the table once the list counts a company; adding one here does not touch the
	// list's own query, so closing the search re-reads it (PREC-3240).
	const closeAddCompany = (open: boolean) => {
		setAddCompanyOpen(open);

		if (!open && listId) {
			dispatch(api.util.invalidateTags([{ type: "List", id: +listId }]));
		}
	};

	return (
		<>
			<TrackerEmptyState
				onAddCompany={trackerId ? () => setAddCompanyOpen(true) : undefined}
				onAddSource={() => navigate(`/app/lists/create/source/${listId}`)}
			/>
			{trackerId && addCompanyOpen && (
				<SearchForCompanyModal
					open={addCompanyOpen}
					setOpen={closeAddCompany}
					trackerId={trackerId}
				/>
			)}
		</>
	);
};
